import { FC, useMemo } from 'react'

import { useNavigate } from '@tanstack/react-router'

import { PERMISSIONS } from '@forto-dev/auth-sdk-models'
import {
  Avatar,
  AvatarFallback,
  Cluster,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@forto-dev/phoenix-ui-core/common'
import { useLocalisation } from '@forto-dev/platform-localisation'

import { useAuth } from '@/features/auth'
import { NotificationWidget } from '@/features/notifications'
import { Features } from '@/utils/featureFlag/features'
import { useFeatureFlag } from '@/utils/featureFlag/useFeatureFlag'
import { getInitialsForName } from '@/utils/initials'

export const Cockpit: FC = () => {
  const navigate = useNavigate()
  const { isFeatureEnabled } = useFeatureFlag()
  const { translate, currentLanguage, supportedLanguages, changeLanguage } = useLocalisation()
  const { user, logout, isCustomerActive, hasPermission } = useAuth()
  const initials = useMemo(() => getInitialsForName(`${user?.firstName ?? ''} ${user?.lastName ?? ''}`), [user])
  const isInternalUser = useMemo(() => {
    const email = (user?.email ?? '').toLowerCase()
    return email.endsWith('@forto.com') || email.endsWith('@freighthub.com')
  }, [user])
  const currentLanguageName = useMemo(
    () => supportedLanguages.find((item) => item.code === currentLanguage)?.name,
    [currentLanguage, supportedLanguages]
  )
  const isCompanyMenuReady = isCustomerActive && !!user
  const canSeeCompanyUsers = isCompanyMenuReady && hasPermission(PERMISSIONS.CUSTOMERS.USERS.READ)
  const canSeeCompanyAccount =
    isCompanyMenuReady && isFeatureEnabled(Features.COMPANY_ACCOUNT_PAGE) && hasPermission(PERMISSIONS.DIRECTORY.READ)
  const canSeeCompanyDocuments = isCompanyMenuReady && hasPermission(PERMISSIONS.DOCUMENTS.CUSTOMERS.READ)
  const canSeeIntegrations =
    isCompanyMenuReady &&
    isFeatureEnabled(Features.INTEGRATIONS_ENABLED) &&
    hasPermission(PERMISSIONS.INTEGRATIONS.READ)
  const showCompanyMenu = canSeeCompanyUsers || canSeeCompanyAccount || canSeeCompanyDocuments || canSeeIntegrations

  return (
    <Cluster gap={8} y="center" data-testid="cockpit">
      <NotificationWidget />
      <DropdownMenu>
        <DropdownMenuTrigger>
          <Avatar>
            <AvatarFallback>{initials}</AvatarFallback>
          </Avatar>
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          <DropdownMenuItem
            onClick={() => {
              navigate({
                to: '/settings',
              })
            }}
          >
            {translate('cockpit.account_settings')}
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => {
              navigate({
                to: '/notification-center/settings',
              })
            }}
          >
            {translate('cockpit.notification_settings')}
          </DropdownMenuItem>
          <DropdownMenuSub>
            <DropdownMenuSubTrigger>{currentLanguageName}</DropdownMenuSubTrigger>
            <DropdownMenuSubContent sideOffset={2} alignOffset={-8}>
              {supportedLanguages.map((lang) => {
                return (
                  <DropdownMenuItem
                    key={lang.code}
                    onClick={() => {
                      changeLanguage(lang.code)
                    }}
                    disabled={currentLanguage === lang.code}
                  >
                    {lang.name}
                  </DropdownMenuItem>
                )
              })}
            </DropdownMenuSubContent>
          </DropdownMenuSub>
          {showCompanyMenu && <DropdownMenuSeparator />}
          {canSeeCompanyUsers && (
            <DropdownMenuItem
              onClick={() => {
                navigate({
                  to: '/company/users',
                })
              }}
            >
              {translate('cockpit.user_management')}
            </DropdownMenuItem>
          )}
          {canSeeCompanyAccount && (
            <DropdownMenuItem
              onClick={() => {
                navigate({
                  to: '/company/account',
                })
              }}
            >
              {translate('cockpit.company_account')}
            </DropdownMenuItem>
          )}
          {canSeeCompanyDocuments && (
            <DropdownMenuItem
              onClick={() => {
                navigate({
                  to: '/company/documents',
                })
              }}
            >
              {translate('cockpit.document_management')}
            </DropdownMenuItem>
          )}
          {canSeeIntegrations && (
            <DropdownMenuItem
              onClick={() => {
                navigate({
                  to: '/integrations',
                })
              }}
            >
              {translate('cockpit.integrations')}
            </DropdownMenuItem>
          )}
          {isInternalUser ? (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() =>
                  window.open(
                    'https://forto.atlassian.net/servicedesk/customer/portal/2/group/23/create/1420',
                    '_blank',
                    'noopener,noreferrer'
                  )
                }
              >
                {translate('cockpit.bugtracker')}
              </DropdownMenuItem>
            </>
          ) : null}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={() => {
              navigate({
                to: '/support',
              })
            }}
          >
            {translate('cockpit.support')}
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={async () => {
              const loginPath = await logout()
              if (!loginPath) return

              navigate({
                to: loginPath,
              })
            }}
          >
            {translate('cockpit.logout')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </Cluster>
  )
}
